import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, HeartHandshake, ArrowRight, Sparkles } from 'lucide-react';
import PsaContent from './PsaContent';

/**
 * Small marketing card + full-screen modal for the PSA. Placed on PublicHome
 * between the hero CTAs and the featured video. The same PsaContent is also
 * mounted on the /psa route.
 */
export default function PsaCard() {
  const [open, setOpen] = useState(false);

  // Lock body scroll while the modal is open + support ESC to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <section
        data-testid="psa-section"
        className="max-w-5xl mx-auto px-4 py-10"
      >
        <div
          className="relative rounded-3xl overflow-hidden shadow-2xl border border-amber-300/30"
          style={{
            backgroundImage:
              'radial-gradient(ellipse at top left, rgba(251,191,36,0.30), transparent 60%), radial-gradient(ellipse at bottom right, rgba(34,197,94,0.28), transparent 60%), linear-gradient(135deg, #0b3d2e 0%, #0f172a 55%, #14532d 100%)',
          }}
        >
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl pointer-events-none"/>
          <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none"/>

          <div className="relative px-6 sm:px-10 py-10 sm:py-14 text-center text-white">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-200 bg-amber-500/10 border border-amber-300/30 rounded-full px-3 py-1">
              <Sparkles size={11}/> A Public Service Announcement
            </div>
            <h2
              data-testid="psa-card-heading"
              className="mt-5 text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[1.05]"
            >
              WHY JOIN THE <span className="text-amber-300">MOVEMENT</span>?
              <span className="block mt-2 text-white/95 text-2xl sm:text-3xl md:text-4xl italic font-serif font-semibold">
                From Consumer to Advocate
              </span>
            </h2>
            <p className="mt-5 text-white/80 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              A short story about why what you spend, share, and stand behind
              matters more than you think — narrated for you.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="psa-learn-more-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-neutral-900 font-bold shadow-xl transition-colors"
              >
                <HeartHandshake size={16}/> Learn More
              </button>
              <Link
                to="/psa"
                data-testid="psa-open-page-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20 transition-colors"
              >
                Open full page <ArrowRight size={14}/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-0 md:p-6 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="psa-heading"
          data-testid="psa-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="relative w-full md:max-w-3xl bg-white md:rounded-3xl shadow-2xl overflow-hidden max-h-screen md:max-h-[90vh] flex flex-col">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="psa-modal-close"
              aria-label="Close"
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-neutral-800 border border-neutral-200 shadow flex items-center justify-center"
            >
              <X size={18}/>
            </button>
            <div className="overflow-y-auto">
              <PsaContent autoPlay={true} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
